import { Link } from 'react-router-dom';

const curriculum = [
  {
    title: 'Class 1: Environment & First Steps',
    sections: [
      {
        label: 'Concepts',
        items: ['Intro to Python, why use it, and real-world applications.'],
      },
      {
        label: 'Setup',
        items: ['Installing Python 3, configuring VS Code, and installing the Python extension.'],
      },
      {
        label: 'Hands-on',
        items: [
          'Writing a "Hello World" script, using the print() function, and utilizing comments (#) for documentation.',
        ],
      },
    ],
  },
  {
    title: 'Class 2: Managing Strings & Text Data',
    sections: [
      {
        label: 'Concepts',
        items: ['String fundamentals and manipulation.'],
      },
      {
        label: 'Operations',
        items: [
          'Combining strings (concatenation), changing case (.upper(), .lower()), and finding characters.',
        ],
      },
      {
        label: 'Hands-on',
        items: ['Building a "Mad Libs" generator to practice input and string formatting.'],
      },
    ],
  },
  {
    title: 'Class 3: Numbers, Math & Type Conversion',
    sections: [
      {
        label: 'Concepts',
        items: ['Integers vs. Floats and the importance of data types.'],
      },
      {
        label: 'Operations',
        items: ['Standard arithmetic, handling user input as numbers, and converting strings to numeric types.'],
      },
      {
        label: 'Hands-on',
        items: [
          'Creating a "Calculator" app that takes user inputs and performs multiple math operations.',
        ],
      },
    ],
  },
  {
    title: 'Class 4: Dates & Defensive Programming',
    sections: [
      {
        label: 'Concepts',
        items: ['Working with the datetime library and understanding errors.'],
      },
      {
        label: 'Logic',
        items: ['Managing date formats and calculating time deltas.'],
      },
      {
        label: 'Resilience',
        items: ['Introduction to try...except blocks to handle runtime errors gracefully.'],
      },
      {
        label: 'Hands-on',
        items: ['Building a "Days Until" countdown timer that doesn\'t crash on bad input.'],
      },
    ],
  },
  {
    title: 'Class 5: Conditional Logic',
    sections: [
      {
        label: 'Concepts',
        items: ['Boolean logic and comparison operators (==, >, !=).'],
      },
      {
        label: 'Branching',
        items: ['Using if, elif, and else to control program flow.'],
      },
      {
        label: 'Hands-on',
        items: [
          'Developing a "Grade Calculator" or "Security Gate" program that grants different access based on user input.',
        ],
      },
    ],
  },
  {
    title: 'Class 6: Advanced Conditions & Logic',
    sections: [
      {
        label: 'Concepts',
        items: ['Combining logic with and, or, and the in operator.'],
      },
      {
        label: 'Complexity',
        items: ['Nested if statements and efficient logical checks.'],
      },
      {
        label: 'Hands-on',
        items: [
          'Building a "Tax Bracket" or "Insurance Quote" simulator requiring multiple criteria to be met.',
        ],
      },
    ],
  },
  {
    title: 'Class 7: Working with Lists & Arrays',
    sections: [
      {
        label: 'Concepts',
        items: ['Storing multiple items in one variable using Lists.'],
      },
      {
        label: 'Operations',
        items: ['Adding/removing items, indexing, and basic slicing.'],
      },
      {
        label: 'Hands-on',
        items: [
          'Creating a "Shopping List Manager" that allows users to add, view, and remove items.',
        ],
      },
    ],
  },
  {
    title: 'Class 8: Loops & Iteration',
    sections: [
      {
        label: 'Concepts',
        items: ['Automating repetitive tasks with for and while loops.'],
      },
      {
        label: 'Iteration',
        items: ['Looping through lists and strings.'],
      },
      {
        label: 'Hands-on',
        items: ['Designing a "Number Guessing Game" where the user has limited tries (using a loop).'],
      },
    ],
  },
  {
    title: 'Class 9: Reusable Functions & Modules',
    sections: [
      {
        label: 'Concepts',
        items: ['Principles of DRY (Don\'t Repeat Yourself).'],
      },
      {
        label: 'Structure',
        items: ['Defining functions, using parameters, and returning values.'],
      },
      {
        label: 'Organization',
        items: ['Importing built-in and external modules.'],
      },
      {
        label: 'Hands-on',
        items: [
          'Refactoring previous projects to use functions for common tasks (like input validation).',
        ],
      },
    ],
  },
  {
    title: 'Class 10: Dictionaries & Web APIs',
    sections: [
      {
        label: 'Concepts',
        items: ['Storing Key-Value pairs with Dictionaries and intro to Virtual Environments.'],
      },
      {
        label: 'The Web',
        items: ['Introduction to calling external APIs (JSON basics).'],
      },
      {
        label: 'Hands-on',
        items: [
          'A "Weather or Quote Finder" that fetches data from a public API and displays it using a formatted dictionary.',
        ],
      },
    ],
  },
];

const PythonProgrammingForBeginners = () => {
  return (
    <div className="bg-white">
      <div className="bg-blue-700 pt-16 pb-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <h1 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              Python Programming for Beginners
            </h1>
            <Link
              to="/courses"
              className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium bg-white text-blue-700 hover:bg-blue-50"
            >
              Back to Courses
            </Link>
          </div>
          <p className="mt-4 text-blue-100 max-w-3xl">
            A structured 10-class curriculum designed to build strong Python fundamentals with hands-on projects.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid gap-6">
          {curriculum.map((lesson) => (
            <div key={lesson.title} className="rounded-lg border border-gray-200 bg-white shadow-sm">
              <div className="p-6">
                <h2 className="text-xl font-semibold text-gray-900">{lesson.title}</h2>
                <div className="mt-4 space-y-4">
                  {lesson.sections.map((section) => (
                    <div key={section.label}>
                      <p className="text-sm font-semibold text-gray-900">{section.label}</p>
                      <ul className="mt-2 space-y-1 text-sm text-gray-600 list-disc pl-5">
                        {section.items.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PythonProgrammingForBeginners;
